import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import singleImage from '../../../images/singleblog.svg';

function mediaUrl(path) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    let relative = String(path).replace(/^\//, '');
    if (!relative.startsWith('uploads/blogs/')) {
        relative = `uploads/blogs/${relative.split('/').pop()}`;
    }
    const root = (process.env.REACT_APP_UPLOAD_URL || '').replace(/\/$/, '');
    return `${root}/${relative}`;
}

function SingleBlogBody() {
    const { id } = useParams(); 
    const [blog, setBlog] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [name, setName] = useState('');
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchBlogDetails = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/blogs/${id}`);
                const row = response.data?.data;
                if (row) {
                    document.title = `${row.title} | Yebokenny Blog`;
                    setBlog({
                        ...row,
                        image_path: row.imagePath,
                        created_at: row.createdAt,
                        details: row.details || '',
                    });
                    setComments((row.commentList || []).map((item) => ({
                        id: item.id,
                        name: item.name,
                        comment: item.comment,
                        created_at: item.createdAt,
                    })));
                } else {
                    setError('Failed to load blog details');
                }
            } catch (err) {
                console.error('Error fetching blog details:', err);
                setError('Error loading blog details.');
            } finally {
                setLoading(false);
            }
        };

        fetchBlogDetails();
    }, [id]);

    useEffect(() => {
        if (!id) return;
        axios.post(`${process.env.REACT_APP_BASE_URL}/blogs/${id}/views`).catch((error) => {
            console.error('Failed to record view:', error);
        });
    }, [id]);

    const getFirstLetter = (str) => str?.charAt(0)?.toUpperCase() || '?';

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !comment.trim()) return;

        setSubmitting(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/blogs/${id}/comments`, {
                name,
                comment
            });

            if (res.data?.data) {
                const saved = res.data.data;
                setComments(prev => [
                    ...prev,
                    {
                        id: saved.id,
                        name: saved.name,
                        comment: saved.comment,
                        created_at: saved.createdAt
                    }
                ]);
                setName('');
                setComment('');
            } else {
                alert('Failed to post comment');
            }
        } catch (err) {
            console.error('Error submitting comment:', err);
            alert('Error submitting comment');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className='px-10 lg:px-20 lg:py-10'>
            {loading ? (
                <p className="text-center text-gray-500">Loading blog...</p>
            ) : error ? (
                <p className="text-center text-red-500">{error}</p>
            ) : (
                <>
                    <div>
                        <img
                            className='mt-5 lg:mt-0 lg:h-[400px] xl:h-[590px] rounded-[8px]'
                            src={mediaUrl(blog.image_path) || singleImage}
                            alt={blog.title}
                        />
                    </div>
                    <div className='mt-2 text-[#5E5E5E] text-[20px] font-[400] text-start'>
                        Published {new Date(blog.created_at).toLocaleDateString()}
                    </div>
                    <div className='flex flex-col space-y-3 text-start mt-3 lg:mt-8'>
                        <div className='text-[30px] lg:text-[48px] font-[600] text-[#0E0E0E] leading-[40px] lg:leading-[67.2px]'>
                            {blog.title}
                        </div>
                        <div dangerouslySetInnerHTML={{ __html: blog.details.replace(/\n/g, '<br/>') }} className='text-[#4F4F4F] text-[18px] font-[400]' />
                    </div>

                    <div className='my-10 rounded-[12px] flex flex-col text-start bg-[#F5F5F5] p-5 space-y-5'>
                        <div className='flex flex-col space-y-1'>
                            <span className='text-[40px] text-[#121212] font-[500]'>Write a comment</span>
                            <span className='text-[#121212] font-[400] text-[16px]'>Share your thoughts, join the conversation, and let us know what you think!</span>
                        </div>
                        <form onSubmit={handleSubmit} className='grid grid-cols-2 gap-x-2 gap-y-3'>
                            <input
                                required
                                type='text'
                                placeholder='Your name'
                                className='col-span-1 rounded-[8px] outline-none text-[#121212] text-[16px] h-[60px] px-3'
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <input
                                type='email'
                                placeholder='Your email (optional)'
                                className='col-span-1 rounded-[8px] outline-none text-[#121212] text-[16px] h-[60px] px-3'
                            />
                            <textarea
                                required
                                placeholder='Your comment'
                                className='col-span-2 rounded-[8px] outline-none text-[#121212] text-[16px] h-[306px] px-3 py-2'
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                            <div className='col-span-2'>
                                <button
                                    type='submit'
                                    disabled={submitting}
                                    className='bg-[#008B39] rounded-[36px] text-white h-[56px] px-20'
                                >
                                    {submitting ? 'Posting...' : 'Post comment'}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className='my-10 flex flex-col space-y-4'>
                        <span className='text-start text-[#121212] text-[40px]'>Comments</span>
                        <div className='flex flex-col space-y-4'>
                            {comments.length === 0 ? (
                                <p className='text-gray-500'>No comments yet. Be the first to comment!</p>
                            ) : (
                                comments.map(({ id, name, created_at, comment }) => (
                                    <div key={id} className='flex flex-col space-y-4'>
                                        <div className='flex flex-row items-center space-x-4'>
                                            <div className='rounded-full bg-[#F7F7F7] h-[52px] w-[52px] grid'>
                                                <span className='my-auto font-[600] text-[#121212] text-[20px]'>{getFirstLetter(name)}</span>
                                            </div>
                                            <div className='flex flex-col space-y-0 text-start'>
                                                <span className='text-[24px] text-[#121212] font-[600]'>{name}</span>
                                                <span className='text-[#6D7076] text-[16px] font-[500]'>{new Date(created_at).toLocaleString()}</span>
                                            </div>
                                        </div>
                                        <div className='text-[16px] font-[400] text-[#6D7076] text-start'>{comment}</div>
                                        <div><hr /></div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default SingleBlogBody;
